import {
  Dropdown,
  LeadsStatusesListData,
  leadsStatusesListListInfiniteOptions,
  PaginatedDropdownList,
  reportsDemoChartListOptions,
  reportsPhoneCallsChartListOptions,
  reportsReasonWeightDiagramRetrieveOptions,
  reportsScoreWeightDiagramRetrieveOptions,
  reportsSourcePageWeightDiagramRetrieveOptions,
  reportsSourceWebsiteWeightDiagramRetrieveOptions,
  reportsSourceWeightDiagramRetrieveOptions,
  reportsStatusWeightDiagramRetrieveOptions,
  SourcesListListData,
  sourcesListListInfiniteOptions
} from '@crm/api'
import { InfiniteSelect, RangePickerFilter } from '@crm/components'
import {
  LeadApplicationsChart,
  LineChart,
  ReportSelect,
  ShareChart
} from '@crm/features/reports/components'
import { defaultFiltersMultipleValues } from '@crm/utils'
import { useQueryUpdate } from '@shared/hooks'
import { useLocales } from '@shared/locales'

import { Button, Col, Flex, Row, Space } from 'antd'
import { useEffect, useMemo } from 'react'

export const ReportDashboard = () => {
  const { params } = useQueryUpdate()

  const { t } = useLocales()
  const { updateQuery } = useQueryUpdate()

  const queryParams = useMemo(
    () => ({
      ...(params.date_from && {
        date_from: params.date_from
      }),
      days: params.days,
      ...(params.date_to && {
        date_to: params.date_to
      }),
      currentYear: !!params.days,
      status: params.status,
      source: params.source
    }),
    [params]
  )

  const sourceValue = defaultFiltersMultipleValues(params?.source, true)
  const leadsStatusValue = defaultFiltersMultipleValues(params?.status, true)

  useEffect(() => {
    if (!params?.days && !params?.date_from && !params?.date_to) {
      updateQuery({ days: 7 })
    }
  }, [])

  return (
    <Flex vertical gap={16}>
      <Flex gap={8} align="start" wrap="wrap">
        <ReportSelect />

        <InfiniteSelect<SourcesListListData, PaginatedDropdownList, Dropdown>
          placeholder={t('contacts.source')}
          className="select-min-width"
          getQueryFunction={sourcesListListInfiniteOptions}
          onChange={(source) => updateQuery({ source })}
          baseOptions={{
            query: {
              preference: params?.source
            }
          }}
          value={sourceValue}
          mode="multiple"
          maxTagCount={1}
          maxTagTextLength={6}
        />

        <InfiniteSelect<LeadsStatusesListData, PaginatedDropdownList, Dropdown>
          placeholder={t('filter.status')}
          className="select-min-width"
          getQueryFunction={leadsStatusesListListInfiniteOptions}
          onChange={(status) => updateQuery({ status })}
          baseOptions={{
            query: {
              preference: params?.status
            }
          }}
          value={leadsStatusValue}
          mode="multiple"
          maxTagCount={1}
          maxTagTextLength={6}
        />

        {!params.days && (
          <RangePickerFilter
            placeholder={[t('filter.date_from'), t('filter.date_to')]}
          />
        )}

        <Space.Compact>
          <Button
            type={!parseInt(params?.days) ? 'primary' : 'default'}
            onClick={() =>
              updateQuery({
                days: undefined
              })
            }
          >
            {t('filter.period')}
          </Button>

          <Button
            type={params.days === '7' ? 'primary' : 'default'}
            onClick={() =>
              updateQuery({ days: 7, date_from: undefined, date_to: undefined })
            }
          >
            7 {t('nomenclature.days')}
          </Button>

          <Button
            type={params.days === '14' ? 'primary' : 'default'}
            onClick={() =>
              updateQuery({
                days: 14,
                date_from: undefined,
                date_to: undefined
              })
            }
          >
            14 {t('nomenclature.days')}
          </Button>

          <Button
            type={params.days === '30' ? 'primary' : 'default'}
            onClick={() =>
              updateQuery({
                days: 30,
                date_from: undefined,
                date_to: undefined
              })
            }
          >
            {t('nomenclature.one_month')}
          </Button>
        </Space.Compact>
      </Flex>

      <LeadApplicationsChart {...queryParams} />

      <Row gutter={20}>
        <Col span={12}>
          <LineChart
            label={t('reports.calls_histogram')}
            getQueryFunction={reportsPhoneCallsChartListOptions}
            {...queryParams}
          />
        </Col>

        <Col span={12}>
          <LineChart
            label={t('reports.demo_histogram')}
            getQueryFunction={reportsDemoChartListOptions}
            {...queryParams}
          />
        </Col>
      </Row>

      <Row gutter={20}>
        <Col span={12}>
          <ShareChart
            name="source-share"
            label={t('reports.share_leads_by_source')}
            subLabel="leads"
            getQueryFunction={reportsSourceWeightDiagramRetrieveOptions}
            {...queryParams}
          />
        </Col>

        <Col span={12}>
          <ShareChart
            name="source-page-share"
            label={t('reports.share_leads_by_page')}
            subLabel={t('menu.leads')}
            getQueryFunction={reportsSourcePageWeightDiagramRetrieveOptions}
            {...queryParams}
          />
        </Col>
      </Row>

      <Row gutter={20}>
        <Col span={12}>
          <ShareChart
            name="score-share"
            label={t('reports.share_leads_by_scoring')}
            subLabel=""
            getQueryFunction={reportsScoreWeightDiagramRetrieveOptions}
            {...queryParams}
          />
        </Col>

        <Col span={12}>
          <ShareChart
            name="reason-share"
            label={t('reports.share_leads_by_reason')}
            subLabel={t('menu.leads')}
            getQueryFunction={reportsReasonWeightDiagramRetrieveOptions}
            {...queryParams}
          />
        </Col>
      </Row>

      <Row gutter={20}>
        <Col span={12}>
          <ShareChart
            name="status-share"
            label={t('reports.share_leads_by_status')}
            subLabel=""
            getQueryFunction={reportsStatusWeightDiagramRetrieveOptions}
            {...queryParams}
          />
        </Col>

        <Col span={12}>
          <ShareChart
            name="source-website-share"
            label={t('reports.share_leads_by_source_website')}
            subLabel={t('menu.leads')}
            getQueryFunction={reportsSourceWebsiteWeightDiagramRetrieveOptions}
            {...queryParams}
          />
        </Col>
      </Row>
    </Flex>
  )
}
